import { camelCase, mapKeys } from 'lodash'

type ColorPalette = Record<string, string | Record<string | number, string>>

/**
 * Define colours here. Through the functions above, they can be convered to the form you need.
 */
export const colorPalette = {
  transparent: 'transparent',
  current: 'currentColor',
  inherit: 'inherit',
  black: '#000000',
  white: '#ffffff',

  'medal-bronze': {
    DEFAULT: '#aa6e29',
    300: '#aa6e29',
    400: '#a26927',
    500: '#956124',
  },

  'medal-silver': {
    DEFAULT: '#9cb0b5',
    300: '#9cb0b5',
    400: '#96abb1',
    500: '#8da4aa',
  },

  'medal-gold': {
    DEFAULT: '#f0ca3c',
    300: '#f0ca3c',
    400: '#e7c018',
    500: '#ddb817',
  },

  'dusty-black': {
    50: '#f3f3f2',
    100: '#CFCFD3',
    200: '#A0A1A4',
    250: '#727174',
    300: '#414348',
    400: '#242221',
    500: '#121110',
  },

  'oceany-blue': {
    50: '#e5f4fb',
    100: '#cdeaf8',
    150: '#b3e0f3',
    200: '#9bd6f0',
    300: '#36ade1',
    325: '#1e98ce',
    350: '#197ba9',
    400: '#206787',
    500: '#0e2b38',
  },

  'cornflower-blue': {
    100: '#DDE9FC',
    325: '#1769ee',
    350: '#0f5ad6',
    400: '#0B45A2',
  },

  'grapey-green': {
    100: '#ebf2cd',
    200: '#d6e59b',
    300: '#aeca36',
    400: '#57651b',
    500: '#2b330e',
  },

  'ms-green': {
    50: '#ecf7e2',
    100: '#d9f0c6',
    150: '#c6e8a9',
    200: '#b4e18d',
    300: '#6bb32e',
    325: '#60A129',
    350: '#508622',
    400: '#355917',
    500: '#1a2c0b',
  },

  'msp-green': {
    300: '#00A45F',
  },

  'forest-green': {
    50: '#eafaea',
    100: '#cbf4cc',
    150: '#b1efb2',
    300: '#22a524',
    350: '#197b1b',
  },

  'fp-purple': {
    300: '#7b62ae',
  },

  'purply-pink': {
    100: '#f2cde2',
    200: '#e59bc5',
    300: '#cb378c',
    350: '#a32a70',
    400: '#661c46',
    500: '#330e23',
  },

  'yolky-yellow': {
    100: '#fff2c0',
    200: '#ffe581',
    300: '#ffcC04',
    400: '#806602',
  },

  'watermelony-red': {
    50: '#feeef2',
    100: '#fec1cf',
    150: '#fca1b7',
    200: '#fc839f',
    250: '#fa446f',
    300: '#f9063f',
    350: '#d10535',
    400: '#7d031f',
    500: '#3e0210',
  },

  'rexy-purple': {
    100: '#d8cbe3',
    200: '#b296c8',
    300: '#652d90',
    400: '#321748',
    500: '#190b25',
  },

  'juicy-orange': {
    50: '#fef3e3',
    100: '#fee7c7',
    200: '#fccf90',
    250: '#ffb68f',
    300: '#f99303',
    350: '#e88a06',
    400: '#f65504',
    500: '#7d3410',
  },

  'deep-orange': {
    50: '#fff3f0',
    350: '#d13100',
  },

  'wl-blue': {
    50: '#EFF3F9',
    100: '#D1DFF4',
    150: '#A6BDE0',
    250: '#4676BE',
    300: '#365F9D',
    400: '#2A4979',
    500: '#1B2F4E',
  },
  'bright-blue': {
    300: '#55C3FE',
    500: '#0192DF',
  },
  'glowy-orange': {
    300: '#F99303',
  },
  'rusty-red': {
    300: '#D96233',
  },
  grey: {
    300: '#e9e9e9',
    400: '#dcdbe1',
    500: '#a7a2c2',
  },
  cyan: {
    100: '#7af9fa',
    150: '#67ddfd',
  },
  'orangy-pink': {
    100: '#ffd7c8',
    200: '#e683c7',
  },
  'ms-prime-yellow': {
    100: '#fcff75',
    200: '#fddd26',
    300: '#fcbc2c',
    400: '#fd9506',
  },
  'muted-blue': {
    200: '#74a3e9',
  },
  blue: {
    50: '#f6f7f9',
    100: '#dde9fc',
    325: '#1769ee',
    350: '#0f5ad6',
    400: '#0b45a2',
  },
  neutral: {
    0: '#ffffff',
    50: '#f2f2f3',
    75: '#e7eaef',
    100: '#c7ceda',
    200: '#939fb1',
    250: '#5c6e8a',
    300: '#2c3f5d',
    400: '#0e1e37',
    500: '#030e1f',
  },
  pink: {
    50: '#f9e7f1',
    100: '#f2cde2',
    300: '#cb378c',
    350: '#a32a70',
    400: '#661c46',
  },
  red: {
    50: '#feeef2',
    100: '#fec1cf',
    200: '#fc839f',
    250: '#fa446f',
    300: '#f9063f',
    350: '#d10535',
  },
  yellow: {
    50: '#fff5cc',
    150: '#ffeb9e',
    200: '#ffe581',
    400: '#806602',
  },
  green: {
    50: '#eafaea',
    150: '#b1efb2',
    200: '#95e997',
    300: '#22a524',
    350: '#197b1b',
  },
  orange: {
    100: '#FEE7C7',
    350: '#E88A06',
    400: '#f65504',
    450: '#BF4B1F',
    500: '#7D3410',
  },
  ocean: {
    100: '#CDEAF8',
    400: '#206787',
  },
} as const satisfies ColorPalette

/**
 * Helpful for defining postcss variables, which are dasherized.
 *
 * Turns ...
 * {
 *   'abc-def': {
 *     123: '#ffffff',
 *     456: '#ffffff'
 *   }
 * }
 * into ...
 * {
 *   'abc-def-123': '#ffffff,
 *   'abc-def-456': '#ffffff
 * }
 */
export function dasherizeColorPalette(colors: ColorPalette): Record<string, string> {
  return Object.entries(colors)
    .flatMap(([color, values]) =>
      typeof values === 'object'
        ? Object.entries(dasherizeColorPalette(values)).map(([number, hex]) => ({
            [color + (number === 'DEFAULT' ? '' : `-${number}`)]: hex,
          }))
        : [{ [`${color}`]: values }],
    )
    .reduce((acc, item) => ({ ...acc, ...item }), {})
}
/**
 * Best for use in app code, where you can access colours easily with dot-notation.
 *
 * Turns ...
 * {
 *   'abc-def': {
 *     123: '#ffffff',
 *     456: '#ffffff'
 *   }
 * }
 * into ...
 * {
 *   abcDef123: '#ffffff',
 *   abcDef456: '#ffffff'
 * }
 */
export function camelizeColorPalette(colors: ColorPalette) {
  return mapKeys(dasherizeColorPalette(colors), (_, key) => camelCase(key))
}

/**
 * A camelized version of the palette for easy use within app code.
 */
export const colors = camelizeColorPalette(colorPalette)
